/**
 * Cookie Policy Page
 */

import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  AppBar,
  Toolbar,
  IconButton
} from '@mui/material';
import {
  ArrowBack,
  FamilyRestroom,
  Cookie,
  Settings,
  Security,
  Analytics
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

function CookiePolicy() {
  const navigate = useNavigate();
  useAuth();

  const lastUpdated = "February 8, 2026";

  return (
    <Box sx={{ flexGrow: 1, minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* App Bar */}
      <AppBar position="static" sx={{ bgcolor: 'primary.main' }}>
        <Toolbar>
          <IconButton
            color="inherit"
            onClick={() => navigate('/')}
            aria-label="Back to home"
            sx={{ 
              minWidth: 48, 
              minHeight: 48, 
              borderRadius: 1,
              padding: '12px',
              marginRight: '8px',
              '& .MuiSvgIcon-root': {
                fontSize: '24px'
              }
            }}
          >
            <ArrowBack />
          </IconButton>
          <FamilyRestroom sx={{ ml: 2, mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Cookie Policy
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ mb: 4 }}>
            <Cookie sx={{ mr: 2, verticalAlign: 'middle' }} />
            Cookie Policy
          </Typography>

          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 4 }}>
            Last updated: {lastUpdated}
          </Typography>

          {/* Introduction */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom>
              1. What Are Cookies?
            </Typography>
            <Typography variant="body1" paragraph>
              Cookies are small text files that are stored on your device when you visit our website or use our app. 
              They help us provide you with a better experience by remembering your preferences and enabling certain features.
            </Typography>
          </Box>

          {/* Types of Cookies */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <Settings sx={{ mr: 1 }} />
              2. Types of Cookies We Use
            </Typography>
            
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              2.1 Essential Cookies
            </Typography>
            <Typography variant="body1" paragraph>
              These cookies are necessary for our app to function properly:
            </Typography>
            <Box component="ul" sx={{ pl: 4 }}>
              <li><strong>Authentication cookies:</strong> Keep you logged in securely</li>
              <li><strong>Session cookies:</strong> Maintain your app session</li>
              <li><strong>Security cookies:</strong> Protect against fraud and unauthorized access</li>
              <li><strong>Preference cookies:</strong> Remember your app settings and preferences</li>
            </Box>

            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              2.2 Functional Cookies
            </Typography>
            <Typography variant="body1" paragraph>
              These cookies enhance your experience:
            </Typography>
            <Box component="ul" sx={{ pl: 4 }}>
              <li><strong>Language preferences:</strong> Remember your preferred language</li>
              <li><strong>Theme settings:</strong> Remember your display preferences</li>
              <li><strong>Family settings:</strong> Remember family member configurations</li>
              <li><strong>Task filters:</strong> Remember your task view preferences</li>
            </Box>

            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              2.3 Analytics Cookies
            </Typography>
            <Typography variant="body1" paragraph>
              These cookies help us understand how you use our app:
            </Typography>
            <Box component="ul" sx={{ pl: 4 }}>
              <li><strong>Usage statistics:</strong> How you navigate through the app</li>
              <li><strong>Feature usage:</strong> Which features you use most</li>
              <li><strong>Performance monitoring:</strong> App speed and reliability</li>
              <li><strong>Error tracking:</strong> Identify and fix bugs</li>
            </Box>
          </Box>

          {/* Third-Party Cookies */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom>
              3. Third-Party Cookies
            </Typography>
            
            <Typography variant="body1" paragraph>
              We use third-party services that may set their own cookies:
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              3.1 Google Services
            </Typography>
            <Typography variant="body1" paragraph>
              • <strong>Google Calendar:</strong> For calendar integration features
              • <strong>Google Analytics:</strong> For usage analytics (if enabled)
              • <strong>Google Fonts:</strong> For displaying fonts
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              3.2 Firebase Services
            </Typography>
            <Typography variant="body1" paragraph>
              • <strong>Authentication:</strong> For secure user login
              • <strong>Database:</strong> For storing your family data
              • <strong>Storage:</strong> For photos and files
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              3.3 Stripe
            </Typography>
            <Typography variant="body1" paragraph>
              • <strong>Payment processing:</strong> For premium subscription payments
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              3.4 Brainstorm AI Kit Analytics
            </Typography>
            <Typography variant="body1" paragraph>
              We use Brainstorm AI Kit to collect anonymized usage analytics:
            </Typography>
            <Box component="ul" sx={{ pl: 4 }}>
              <li><strong>Pageview tracking:</strong> Pages visited and time on page</li>
              <li><strong>Interaction tracking:</strong> Clicks, scroll depth, and form submissions</li>
              <li><strong>Performance metrics:</strong> Page load times and site performance</li>
              <li><strong>Device information:</strong> Browser type and device category</li>
              <li><strong>Location data:</strong> City-level only (IP addresses are anonymized)</li>
            </Box>
            <Typography variant="body1" paragraph sx={{ mt: 1 }}>
              This data helps us improve your experience. No personal identification is collected through these 
              analytics. For more information, see the{' '}
              <a href="https://brainstormaikit.com/privacy" target="_blank" rel="noopener noreferrer">
                Brainstorm AI Kit Privacy Policy
              </a>.
            </Typography>
          </Box>

          {/* Cookie Management */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <Security sx={{ mr: 1 }} />
              4. Managing Cookies
            </Typography>
            
            <Typography variant="body1" paragraph>
              You have several options for managing cookies:
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              4.1 Browser Settings
            </Typography>
            <Typography variant="body1" paragraph>
              You can control cookies through your browser settings:
            </Typography>
            <Box component="ul" sx={{ pl: 4 }}>
              <li><strong>Chrome:</strong> Settings > Privacy and security > Cookies and other site data</li>
              <li><strong>Firefox:</strong> Options > Privacy & Security > Cookies and Site Data</li>
              <li><strong>Safari:</strong> Preferences > Privacy > Manage Website Data</li>
              <li><strong>Edge:</strong> Settings > Cookies and site permissions > Cookies and site data</li>
            </Box>

            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              4.2 App Settings
            </Typography>
            <Typography variant="body1" paragraph>
              You can manage some cookies through our app settings:
            </Typography>
            <Box component="ul" sx={{ pl: 4 }}>
              <li>Disable analytics cookies in app preferences</li>
              <li>Clear stored data in account settings</li>
              <li>Revoke third-party service access</li>
            </Box>

            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              4.3 Impact of Disabling Cookies
            </Typography>
            <Typography variant="body1" paragraph>
              Disabling cookies may affect app functionality:
            </Typography>
            <Box component="ul" sx={{ pl: 4 }}>
              <li>You may need to log in repeatedly</li>
              <li>Your preferences may not be saved</li>
              <li>Some features may not work properly</li>
              <li>Third-party integrations may be limited</li>
            </Box>
          </Box>

          {/* Children and Cookies */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom>
              5. Children and Cookies
            </Typography>
            
            <Typography variant="body1" paragraph>
              We are committed to protecting children's privacy:
            </Typography>

            <Box component="ul" sx={{ pl: 4 }}>
              <li>Children under 13 cannot independently manage cookies</li>
              <li>Parents control all cookie settings for child accounts</li>
              <li>We do not use cookies for advertising to children</li>
              <li>Analytics cookies are disabled for child accounts</li>
            </Box>
          </Box>

          {/* Data Retention */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <Analytics sx={{ mr: 1 }} />
              6. Cookie Retention
            </Typography>
            
            <Typography variant="body1" paragraph>
              Different cookies have different lifespans:
            </Typography>

            <Box component="ul" sx={{ pl: 4 }}>
              <li><strong>Session cookies:</strong> Deleted when you close your browser</li>
              <li><strong>Persistent cookies:</strong> Remain for up to 2 years</li>
              <li><strong>Authentication cookies:</strong> Expire after 30 days of inactivity</li>
              <li><strong>Preference cookies:</strong> Remain until you change settings</li>
            </Box>
          </Box>

          {/* Updates to Policy */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom>
              7. Updates to This Cookie Policy
            </Typography>
            
            <Typography variant="body1" paragraph>
              We may update this Cookie Policy from time to time. We will notify you of any material changes by:
            </Typography>

            <Box component="ul" sx={{ pl: 4 }}>
              <li>Posting the updated policy on our website</li>
              <li>Sending email notifications to account holders</li>
              <li>Showing a notification in the app</li>
            </Box>
          </Box>

          {/* Contact Information */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom>
              8. Contact Us About Cookies
            </Typography>
            
            <Typography variant="body1" paragraph>
              If you have questions about our use of cookies, please contact us:
            </Typography>

            <Typography variant="body1">
              <strong>Email:</strong> privacy@earn-echore.app<br />
              <strong>Subject:</strong> Cookie Policy Inquiry<br />
              <strong>Response Time:</strong> We will respond within 7 business days
            </Typography>
          </Box>

          <Box sx={{ mt: 4, p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
            <Typography variant="body2" align="center">
              <strong>This Cookie Policy is effective as of {lastUpdated}</strong>
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}

export default CookiePolicy;











